import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { take } from 'rxjs';
import { CardSetTransformationService, CoreService, DataService, DialogService, FormNavigationService, SaveDataRequest, SpinnerService } from 'dspx-core';
import { API_ENDPOINT_KEYS, API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { APP_DATA_KEYS, POP_UP_CODES, READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS, SERVER_ERROR_POP_UP_CODES } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES, VERSIONS } from '../../constants/templates.constants';
import { AppDataService } from '../../services/app-data.service';
import { ErrorService } from '../../services/error.service';
import { FormDataService } from '../../services/form-data.service';
import { HtmlHelperService } from '../../services/html-helper.service';
import { BaseCardSetDetailComponent } from "../base-card-set-detail-component/base-card-set-detail.component";

@Component({
  selector: 'app-integrity-officer',
  templateUrl: './integrity-officer.component.html',
  styleUrl: './integrity-officer.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class IntegrityOfficerComponent extends BaseCardSetDetailComponent  implements OnInit, OnDestroy {

  fieldTypeReplacements = CardSetTransformationService.cardSetWithFlatTabsFieldTypeReplacements;

  id = '';
  formServiceEndpoint: string = API_ENDPOINT_KEYS.getFormConfig;
  templateId: number = TEMPLATES.integrityOfficer;
  versionNo: number = VERSIONS.latest;
  activeDialog: string;
  editing = false;

  constructor(
    protected router: Router,
    protected appDataService: AppDataService,
    protected coreService: CoreService,
    protected formDataService: FormDataService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    private route: ActivatedRoute,
    private errorService: ErrorService,
    private dataService: DataService,
    private spinnerService: SpinnerService,
    private formNavigationService: FormNavigationService) {
    super(router, appDataService, coreService, formDataService, htmlHelperService, dialogService);
  }

  ngOnInit(): void {
    this.registerSubscription(
      this.route.paramMap.subscribe((paramMap: ParamMap) => {
        const id = paramMap.get('id');
        if (id) {
          this.id = id;
          this.formServiceEndpoint = API_ENDPOINT_KEYS.getIntegrityOfficerConfigAndResponse;
          this.editing = true;
        }
        else {
          this.resetDirtyFlagAfterLoading();
        }
      })
    );

    this.formNavigationService.onNavigation().subscribe(event => {
      setTimeout(() => {
        this.overrideHtmlTitleSetByDspxFramework();
      })
    });

    this.coreService.conditionFunctions['isEditing'] = () => this.editing;
    this.coreService.conditionFunctions['isReadOnly'] = () => true;
    this.coreService.applicationFunctions['canSave'] = () => this.isFormEditable;
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  formLoaded(event) {
    if (this.baseForm && this.baseForm.model) {
      this.appDataService.setAppData(APP_DATA_KEYS.previousModel, JSON.parse(JSON.stringify(this.baseForm.model)));
    }

    this.isFormEditable = !!event?.isFormEditable;

    this.ensurePageActionButtonsAreEnabledOnFormLoad(READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS.integrityOfficer);
    this.handlePostFormLoadActions(event);
  }

  save(event: any) {
    const saveDataRequest: SaveDataRequest = {
      template: this.templateId,
      versionNumber: this.versionNo,
      id: this.id,
      response: event.model,
      expectedLastUpdateTime: event.lastUpdatedTime,
      isSubmit: false
    }
    if (!event.isFormValid) {
      this.baseForm.markAllFieldsAsTouched();
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.dialogService.showDialog(POP_UP_CODES.common.validationError);
      }
      else {
        this.saveData(saveDataRequest);
      }
    }
    else {
      this.saveData(saveDataRequest);
    }
  }

  cancel(event: any) {
    this.navigateBackToDashboard();
  }

  dialogSubmitted(event: any) {
    if (this.activeDialog === POP_UP_CODES.common.saveSuccessful) {
      this.navigateBackToDashboard(true);
    }
  }

  private navigateBackToDashboard(ignoreUnsavedChanges = false): void {
    this.navigate(['/', ROUTE_CONSTANTS.integrityOfficer.list], ignoreUnsavedChanges);
  }

  private saveData(request: SaveDataRequest) {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.dataService.saveData(API_ENDPOINTS.integrityOfficer.save, request)
        .pipe(take(1), this.errorService.errorHandler())
        .subscribe(data => {
          if (data?.status === 'Success') {
            this.spinnerService.hideSpinner();
            if (SERVER_ERROR_POP_UP_CODES.saveIntegrityOfficer.includes(data.message)) {
              this.activeDialog = data.message;
            }
            else {
              this.id = data.id;
              this.activeDialog = POP_UP_CODES.common.saveSuccessful;
            }
            this.dialogService.showDialog(this.activeDialog);
          }
        })
    );
  }
}
